import { useMemo, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { usePrepGuide } from '../prep/hooks';
import type { FocusArea, GroundedIn, Priority } from '../prep/api';
import './livecoach.scss';

/* inline icons (these map to @kula-ai/icons in production) */
const Check = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    {...p}
  >
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Chevron = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Collapse = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GROUNDED_LABEL: Record<GroundedIn, string> = {
  scorecard_question: 'Scorecard',
  job_description: 'Job description',
  candidate_background: 'Background',
  prior_round_gap: 'Prior round',
};

// Labels mirror the prep guide (PrepBrief) so the rail and the prep page read identically.
const PRIORITY_STYLE: Record<
  Priority,
  { label: string; bg: string; color: string }
> = {
  must_cover: {
    label: 'Critical',
    bg: 'status-2-light',
    color: 'status-2-dark',
  },
  core: { label: 'Important', bg: 'primary-50', color: 'primary-700' },
  optional: {
    label: 'Good to cover',
    bg: 'secondary-50',
    color: 'secondary-600',
  },
};

// How the grounding source reads as a caption (mirrors PrepBrief's hover citation).
const SRC_CAPTION: Record<GroundedIn, string> = {
  scorecard_question: "From this round's scorecard",
  job_description: 'Required by the job description',
  candidate_background: "From the candidate's background",
  prior_round_gap: 'Left unverified by an earlier round',
};

// A merged scorecard area cites several questions ('; '-joined) — split them so each renders as its own
// bullet (matching the prep page) instead of one run-on blob.
const sourceParts = (area: FocusArea): string[] =>
  area.grounded_in === 'scorecard_question'
    ? area.source_ref
        .split(/;\s+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : [area.source_ref].filter(Boolean);

type RowStatus = 'covered' | 'in_progress' | 'pending';

// The label under each area mirrors the PREP guide's priority (Critical / Important / Good to cover) — same
// words, same hierarchy — until there's coverage signal. Green/amber are reserved for the coverage circle, so
// priorities use a purple→gray hierarchy instead of the prep's pill colours (green text would clash + read poorly).
const PRIORITY_LABEL: Record<Priority, { text: string; color: string }> = {
  must_cover: { text: 'Critical', color: 'primary-600' },
  core: { text: 'Important', color: 'secondary-700' },
  optional: { text: 'Good to cover', color: 'secondary-400' },
};

function rowLabel(
  status: RowStatus,
  priority: Priority
): { text: string; color: string } {
  if (status === 'covered') return { text: 'covered', color: 'status-4' };
  if (status === 'in_progress')
    return { text: 'in progress', color: 'status-3' };
  return PRIORITY_LABEL[priority] ?? PRIORITY_LABEL.core;
}

// A checklist circle (matches the approved design). Covered → filled green with a white check. Not covered →
// a clean hollow ring. Priority (must-cover vs normal) is carried by the label, never a second ring colour.
function StatusCircle({ status }: { status: RowStatus }) {
  if (status === 'covered') {
    return (
      <Flex
        flex="none"
        align="center"
        justify="center"
        w="22px"
        h="22px"
        mt="1px"
        borderRadius="full"
        bg="status-4"
        color="base-white"
      >
        <Check />
      </Flex>
    );
  }
  if (status === 'in_progress') {
    return (
      <Box
        flex="none"
        w="22px"
        h="22px"
        mt="1px"
        borderRadius="full"
        border="2px solid"
        borderColor="status-3"
        bg="rgba(250,162,56,0.16)"
      />
    );
  }
  return (
    <Box
      flex="none"
      w="22px"
      h="22px"
      mt="1px"
      borderRadius="full"
      border="2px solid"
      borderColor="secondary-200"
    />
  );
}

function FocusRow({ area, status }: { area: FocusArea; status: RowStatus }) {
  const [open, setOpen] = useState(false);
  const prio = PRIORITY_STYLE[area.priority] ?? PRIORITY_STYLE.core;
  const label = rowLabel(status, area.priority);
  const srcParts = sourceParts(area);
  const srcCaption =
    area.grounded_in === 'scorecard_question' && srcParts.length > 1
      ? `Covers ${srcParts.length} scorecard questions`
      : (SRC_CAPTION[area.grounded_in] ?? '');
  return (
    <Box borderBottom="1px solid" borderColor="secondary-75">
      <Flex
        as="button"
        w="100%"
        align="flex-start"
        gap="10px"
        py="11px"
        textAlign="left"
        onClick={() => setOpen((o) => !o)}
        _hover={{ bg: 'secondary-25' }}
      >
        <StatusCircle status={status} />
        <Box flex={1} minW={0}>
          <KText
            size="sm-semi-bold"
            color={status === 'covered' ? 'secondary-500' : 'secondary-900'}
            fontSize="13px"
            fontWeight={600}
            lineHeight="1.35"
            textDecoration={status === 'covered' ? 'line-through' : 'none'}
          >
            {area.area}
          </KText>
          <KText
            size="xs-regular"
            fontSize="11px"
            fontWeight={600}
            color={label.color}
            mt="2px"
          >
            {label.text}
          </KText>
        </Box>
        <Box
          flex="none"
          mt="1px"
          color="secondary-400"
          transform={open ? 'rotate(90deg)' : 'none'}
          transition="transform 0.15s"
        >
          <Chevron />
        </Box>
      </Flex>

      {open && (
        <Box pb="12px" pl="28px" pr="4px">
          <Flex align="center" gap="6px" mb="7px" flexWrap="wrap">
            <Box
              bg={prio.bg}
              color={prio.color}
              px="7px"
              py="2px"
              borderRadius="5px"
              fontSize="9.5px"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing="0.04em"
            >
              {prio.label}
            </Box>
            <Box
              bg="secondary-50"
              color="secondary-600"
              px="7px"
              py="2px"
              borderRadius="5px"
              fontSize="9.5px"
              fontWeight={600}
            >
              {GROUNDED_LABEL[area.grounded_in] ?? area.grounded_in}
            </Box>
          </Flex>
          {area.why && (
            <KText
              size="xs-regular"
              color="secondary-700"
              fontSize="12px"
              lineHeight="1.5"
            >
              {area.why}
            </KText>
          )}
          {srcParts.length > 0 && (
            <Box mt="9px">
              <KText
                size="xs-bold"
                color="secondary-500"
                fontSize="9.5px"
                fontWeight={700}
                letterSpacing="0.04em"
                textTransform="uppercase"
                mb="5px"
              >
                {srcCaption}
              </KText>
              <Flex
                direction="column"
                gap="6px"
                pl="9px"
                borderLeft="2px solid"
                borderColor="secondary-200"
              >
                {srcParts.map((part, idx) => (
                  <KText
                    key={idx}
                    size="xs-regular"
                    color="secondary-500"
                    fontSize="11.5px"
                    fontStyle="italic"
                    lineHeight="1.45"
                  >
                    {part}
                  </KText>
                ))}
              </Flex>
            </Box>
          )}
          {area.suggested_owner && (
            <KText
              size="xs-regular"
              color="secondary-500"
              fontSize="11px"
              mt="7px"
            >
              Owner: {area.suggested_owner}
            </KText>
          )}
        </Box>
      )}
    </Box>
  );
}

interface Props {
  interviewId: number | string | undefined;
  // Focus-area names the coach has marked covered / is currently assessing (from the coverage stream).
  coveredAreas: string[];
  inProgressAreas?: string[];
}

// The collapsible left rail: a top prep-guide pill (about + prior rounds), the focus-area checklist that
// ticks off as the coach marks areas covered, and the prep callouts at the bottom.
export function LiveCoachRail({
  interviewId,
  coveredAreas,
  inProgressAreas = [],
}: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const { data: guide } = usePrepGuide(interviewId);
  const sections = guide?.sections ?? null;

  const focusAreas = sections?.focus_areas ?? [];
  const callouts = sections?.callouts ?? [];

  // Case-insensitive lookups — the coverage names originate from the same prep guide, but normalize to be
  // safe against trivial casing/whitespace drift.
  const norm = (s: string) => s.trim().toLowerCase();
  const coveredSet = useMemo(
    () => new Set(coveredAreas.map(norm)),
    [coveredAreas]
  );
  const inProgressSet = useMemo(
    () => new Set(inProgressAreas.map(norm)),
    [inProgressAreas]
  );
  const statusOf = (area: FocusArea): RowStatus => {
    const key = norm(area.area);
    if (coveredSet.has(key)) return 'covered';
    if (inProgressSet.has(key)) return 'in_progress';
    return 'pending';
  };
  const coveredCount = focusAreas.filter((a) =>
    coveredSet.has(norm(a.area))
  ).length;

  if (collapsed) {
    return (
      <Flex
        flex="none"
        w="46px"
        direction="column"
        align="center"
        py="16px"
        borderRight="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        <Flex
          as="button"
          align="center"
          justify="center"
          w="30px"
          h="30px"
          borderRadius="8px"
          color="secondary-600"
          _hover={{ bg: 'secondary-50', color: 'secondary-900' }}
          onClick={() => setCollapsed(false)}
          transform="rotate(180deg)"
          title="Show round plan"
        >
          <Collapse />
        </Flex>
        {focusAreas.length > 0 && (
          <KText
            size="xs-semi-bold"
            color="secondary-500"
            fontSize="11px"
            fontWeight={700}
            letterSpacing="0.05em"
            mt="14px"
            whiteSpace="nowrap"
            sx={{ writingMode: 'vertical-rl' }}
          >
            <Box as="span" color="status-4">
              {coveredCount}
            </Box>
            {`/${focusAreas.length} covered`}
          </KText>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      flex="none"
      w="278px"
      direction="column"
      h="100%"
      minH={0}
      borderRight="1px solid"
      borderColor="secondary-100"
      bg="base-white"
    >
      {/* header + collapse */}
      <Flex align="center" gap="8px" px="16px" pt="16px" pb="10px" flex="none">
        <KText
          size="sm-bold"
          color="secondary-950"
          fontSize="14px"
          fontWeight={700}
        >
          This round
        </KText>
        <Box flex={1} />
        <Flex
          as="button"
          align="center"
          justify="center"
          w="28px"
          h="28px"
          borderRadius="8px"
          color="secondary-500"
          _hover={{ bg: 'secondary-50', color: 'secondary-900' }}
          onClick={() => setCollapsed(true)}
          title="Hide round plan"
        >
          <Collapse />
        </Flex>
      </Flex>

      {/* focus-area checklist */}
      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        className="livecoach-scroll"
        px="16px"
      >
        <Box
          position="sticky"
          top={0}
          bg="base-white"
          pt="4px"
          pb="8px"
          mb="2px"
          zIndex={1}
        >
          <Flex align="center" gap="8px">
            <KText
              size="xs-bold"
              color="secondary-500"
              fontSize="10px"
              fontWeight={700}
              letterSpacing="0.06em"
              textTransform="uppercase"
            >
              Focus areas
            </KText>
            <Box flex={1} />
            {focusAreas.length > 0 && (
              <KText
                size="xs-bold"
                fontSize="12px"
                fontWeight={700}
                color={coveredCount > 0 ? 'status-4' : 'secondary-500'}
              >
                {coveredCount}/{focusAreas.length}
              </KText>
            )}
          </Flex>
          <KText
            size="xs-regular"
            color="secondary-400"
            fontSize="10.5px"
            mt="2px"
          >
            Ticks off as signal arrives.
          </KText>
        </Box>
        {focusAreas.length === 0 ? (
          <KText
            size="xs-regular"
            color="secondary-500"
            fontSize="12px"
            py="10px"
          >
            {guide
              ? 'No focus areas in the prep guide.'
              : 'Loading the prep guide…'}
          </KText>
        ) : (
          focusAreas.map((area, i) => (
            <FocusRow
              key={`${area.area}-${i}`}
              area={area}
              status={statusOf(area)}
            />
          ))
        )}
      </Box>

      {/* callouts */}
      {callouts.length > 0 && (
        <Box
          flex="none"
          borderTop="1px solid"
          borderColor="secondary-100"
          px="16px"
          py="12px"
          maxH="34%"
          overflowY="auto"
          className="livecoach-scroll"
        >
          <KText
            size="xs-bold"
            color="secondary-500"
            fontSize="10px"
            fontWeight={700}
            letterSpacing="0.06em"
            textTransform="uppercase"
            mb="8px"
          >
            Watch for
          </KText>
          <Flex direction="column" gap="9px">
            {callouts.map((c, i) => (
              <Box key={`${c.label}-${i}`}>
                <KText
                  size="xs-semi-bold"
                  color="secondary-800"
                  fontSize="12px"
                  fontWeight={600}
                >
                  {c.label}
                </KText>
                <KText
                  size="xs-regular"
                  color="secondary-600"
                  fontSize="11.5px"
                  lineHeight="1.45"
                  mt="2px"
                >
                  {c.body}
                </KText>
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Flex>
  );
}

export default LiveCoachRail;
